import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Card, CardActionArea, CardContent, Grid, Typography, Button } from '@mui/material';
import { ArrowBack, Category as CategoryIcon } from '@mui/icons-material';
import { navigationMenuItems } from '../config/navigationMenu';
import { navigateWithAdminBack, clearAdminBackTo } from '../utils/adminNavigationContext';

export interface ModuloFuncionalidadItem {
  text: string;
  path: string;
  description?: string;
  icon?: React.ReactNode;
}

interface ModuloFuncionalidadesProps {
  moduloPath?: string;
  items?: ModuloFuncionalidadItem[];
  /** 1 = tamaño estándar; 0.75 = ~75 % altura/padding y grilla más densa; valores menores = más compacto. */
  cardScale?: number;
  showBackButton?: boolean;
  backPath?: string;
  backLabel?: string;
  /** Origen en Administración para "Volver al Menu" en pantallas hijas. */
  adminBackTo?: string;
}

const COLORES_ICONOS_SUBMENU = [
  '#22a36a',
  '#00a0b9',
  '#8b5cf6',
  '#ef4444',
  '#0ea5e9',
  '#64748b',
  '#14b8a6',
  '#7c3aed',
];

const CONTABILIDAD_ACCESOS_OCULTOS = new Set([
  '/contabilidad/imputables',
  '/contabilidad/noimputables',
]);

const ModuloFuncionalidades: React.FC<ModuloFuncionalidadesProps> = ({
  moduloPath,
  items: customItems,
  cardScale = 1,
  showBackButton = true,
  backPath = '/menu-principal',
  backLabel = 'Volver al Menu Principal',
  adminBackTo,
}) => {
  const navigate = useNavigate();
  const s = Math.min(1, Math.max(0.5, cardScale));
  const gridSm = s >= 1 ? 12 : 6;
  const gridMd = s >= 1 ? 6 : s >= 0.75 ? 4 : 3;
  const gridLg = s >= 1 ? 4 : s >= 0.75 ? 3 : 2;
  const minHx = 240 * s;
  const minHm = 260 * s;
  const contentPad = 3 * s;
  const mbIcon = 2 * s;
  const mbTitle = 1.5 * s;
  const descRem = 0.9 + (1.05 - 0.9) * Math.min(1, Math.max(0, (s - 0.5) / 0.5));

  const modulo = moduloPath
    ? navigationMenuItems.find((item) => item.path === moduloPath)
    : undefined;

  const subItemsVisibles =
    moduloPath === '/contabilidad'
      ? (modulo?.subItems ?? []).filter((item) => !CONTABILIDAD_ACCESOS_OCULTOS.has(item.path))
      : (modulo?.subItems ?? []);

  const items: ModuloFuncionalidadItem[] = customItems
    ?? subItemsVisibles.map((item) => ({
      text: item.text,
      path: item.path,
      icon: item.icon,
      description: item.subItems?.length
        ? item.subItems.map((sub) => sub.text).join(' · ')
        : `Accedé a ${item.text.toLowerCase()} de ${modulo?.text.toLowerCase() ?? 'este módulo'}.`,
    }));

  if (!customItems && (!modulo || !subItemsVisibles.length)) {
    return (
      <Box sx={{ p: { xs: 2, md: 3 } }}>
        <Typography color="text.secondary">
          No hay funcionalidades configuradas para este módulo.
        </Typography>
      </Box>
    );
  }

  if (!items.length) {
    return (
      <Box sx={{ p: { xs: 2, md: 3 } }}>
        <Typography color="text.secondary">
          No hay funcionalidades configuradas para este módulo.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, bgcolor: '#f3f4f6', minHeight: '100vh' }}>
      {showBackButton && (
        <Box sx={{ mb: 2 }}>
          <Button
            variant="contained"
            startIcon={<ArrowBack />}
            onClick={() => navigate(backPath)}
            sx={{ textTransform: 'none' }}
          >
            {backLabel}
          </Button>
        </Box>
      )}
      <Grid container spacing={2.5}>
        {items.map((item, index) => (
          <Grid item xs={12} sm={gridSm} md={gridMd} lg={gridLg} key={item.path}>
            <Card
              sx={{
                borderRadius: 3,
                border: '1px solid #e5e7eb',
                boxShadow: '0 2px 10px rgba(15, 23, 42, 0.06)',
                height: '100%',
                minHeight: { xs: minHx, sm: minHm },
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                  transform: 'translateY(-3px)',
                  boxShadow: '0 8px 22px rgba(15, 23, 42, 0.12)',
                },
              }}
            >
              <CardActionArea
                onClick={() => {
                  if (adminBackTo) {
                    navigateWithAdminBack(navigate, item.path, adminBackTo);
                    return;
                  }
                  clearAdminBackTo();
                  navigate(item.path);
                }}
                sx={{ height: '100%' }}
              >
                <CardContent sx={{ p: contentPad }}>
                  <Box
                    sx={{
                      width: 60,
                      height: 60,
                      borderRadius: 2,
                      bgcolor: COLORES_ICONOS_SUBMENU[index % COLORES_ICONOS_SUBMENU.length],
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#fff',
                      mb: mbIcon,
                      '& .MuiSvgIcon-root': {
                        fontSize: 30,
                      },
                    }}
                  >
                    {item.icon ?? <CategoryIcon />}
                  </Box>

                  <Typography
                    variant={s < 0.72 ? 'h6' : 'h5'}
                    sx={{ fontWeight: 700, color: '#111827', mb: mbTitle }}
                  >
                    {item.text}
                  </Typography>
                  <Typography
                    sx={{
                      color: '#6b7280',
                      fontSize: `${descRem}rem`,
                    }}
                  >
                    {item.description}
                  </Typography>
                </CardContent>
              </CardActionArea>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default ModuloFuncionalidades;
